import React, { useRef, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUploadFileMutation } from '@/store/api/storageApi';
import { useCreateEventMutation, useUpdateEventMutation, Event } from '@/store/api/eventsApi';
import { useNotifications } from '@/hooks/useNotifications';
import { Loader2, Upload, Globe, Link2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import JoditEditor from 'jodit-react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required.').max(150, 'Title cannot exceed 150 characters.'),
  description: z.string().min(1, 'Description is required.'),
  seats: z.number().min(1).optional().or(z.literal('')),
  registrationLink: z.string().url('Invalid URL').optional().or(z.literal('')),
  venue: z.string().max(250, 'Venue cannot exceed 250 characters.').optional().or(z.literal('')),
  imageUrl: z.string().url('Invalid URL').optional().or(z.literal('')),
  eventDate: z.date({ required_error: 'Event date is required.' }),
  status: z.enum(['Draft', 'Published']),
});

type EventFormValues = z.infer<typeof eventSchema>;

interface EventFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventToEdit?: Partial<Event> | null;
}

export function EventFormModal({ isOpen, onClose, eventToEdit }: EventFormModalProps) {
  const [createEvent] = useCreateEventMutation();
  const [updateEvent] = useUpdateEventMutation();
  const [uploadFile] = useUploadFileMutation();
  const { success: notifySuccess, error: notifyError } = useNotifications();

  const [isUploading, setIsUploading] = useState(false);
  const [thumbnailMode, setThumbnailMode] = useState<'url' | 'file'>('url');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [formKey, setFormKey] = useState(0);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    mode: 'onBlur',
    defaultValues: {
      title: '',
      description: '',
      seats: '',
      registrationLink: '',
      venue: '',
      imageUrl: '',
      eventDate: new Date(),
      status: 'Draft',
    },
  });

  const watchImageUrl = watch('imageUrl');

  useEffect(() => {
    if (isOpen) {
      if (eventToEdit && Object.keys(eventToEdit).length > 0) {
        reset({
          title: eventToEdit.title || '',
          description: eventToEdit.description || '',
          seats: eventToEdit.seats || '',
          registrationLink: eventToEdit.registrationLink || '',
          venue: eventToEdit.venue || '',
          imageUrl: eventToEdit.imageUrl || '',
          eventDate: eventToEdit.eventDate ? new Date(eventToEdit.eventDate) : new Date(),
          status: eventToEdit.status || 'Draft',
        });
      } else {
        reset({
          title: '',
          description: '',
          seats: '',
          registrationLink: '',
          venue: '',
          imageUrl: '',
          eventDate: new Date(),
          status: 'Draft',
        });
      }
      setFormKey((prev) => prev + 1);
    }
  }, [isOpen, eventToEdit, reset]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const result = await uploadFile(file).unwrap();
      setValue('imageUrl', result.url, { shouldValidate: true });
      notifySuccess('Image uploaded successfully');
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onSubmit = async (data: EventFormValues) => {
    try {
      const payload = {
        title: data.title.trim(),
        description: data.description.trim(),
        eventDate: data.eventDate.toISOString(),
        registrationLink: data.registrationLink || null,
        venue: data.venue || null,
        imageUrl: data.imageUrl || null,
        status: data.status,
        seats: typeof data.seats === 'number' ? data.seats : null,
        acceptingJoins: true,
      };

      if (eventToEdit?.id) {
        await updateEvent({
          id: eventToEdit.id,
          body: payload,
        }).unwrap();
      } else {
        await createEvent(payload).unwrap();
      }

      notifySuccess('Event saved successfully.');
      onClose();
    } catch (err: any) {
      const msg = err?.data?.message;
      notifyError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save. Please try again.');
    }
  };

  const joditConfig = React.useMemo(
    () => ({
      readonly: false,
      height: 400,
      placeholder: 'Details about the event...',
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'ul',
        'ol',
        '|',
        'font',
        'fontsize',
        'brush',
        'paragraph',
        '|',
        'image',
        'table',
        'link',
        '|',
        'align',
        'undo',
        'redo',
        'hr',
        'eraser',
      ],
      removeButtons: ['source', 'fullsize', 'about', 'print'],
      uploader: {
        url: `${import.meta.env.VITE_API_BASE_URL || '/api'}/upload`,
        format: 'json',
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        buildData: (data: FormData) => {
          if (data.get && data.get('files[0]')) {
            const file = data.get('files[0]');
            if (file) {
              data.delete('files[0]');
              data.append('file', file);
            }
          }
          return data;
        },
        isSuccess: (resp: any) => !!resp.url,
        process: (resp: any) => {
          const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002';
          const fileUrl = resp.url.startsWith('http')
            ? resp.url
            : `${baseUrl}${resp.url.startsWith('/') ? '' : '/'}${resp.url}`;
          return {
            files: [fileUrl],
            path: '',
            baseurl: '',
            error: 0,
            msg: '',
          };
        },
        defaultHandlerSuccess: function (this: any, data: any) {
          if (data.files && data.files.length) {
            this.s.insertImage(data.files[0]);
          }
        },
      },
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
    }),
    [token]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={eventToEdit?.id ? 'Edit Event' : 'Add Event'}>
      <form key={formKey} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Title <span className="text-red-500">*</span>
          </label>
          <Input placeholder="e.g. Annual Tech Conference" {...register('title')} maxLength={150} />
          <div className="flex justify-between mt-1">
            <div className="flex-1">
              {errors.title && <p className="text-red-500 text-xs">{errors.title.message}</p>}
            </div>
            <p className="text-xs text-muted-foreground ml-4 shrink-0">
              {watch('title')?.length || 0}/150
            </p>
          </div>
        </div>

        <div className="space-y-1.5 flex flex-col">
          <label className="text-sm font-medium">
            Description <span className="text-red-500">*</span>
          </label>
          <div className="rounded-lg overflow-hidden border border-input bg-white text-black jodit-wrapper">
            <style
              dangerouslySetInnerHTML={{
                __html: `
              .jodit-wrapper ul, .jodit-wysiwyg ul { list-style-type: disc !important; list-style-position: inside !important; padding-left: 1.5rem !important; margin-bottom: 1rem !important; }
              .jodit-wrapper ol, .jodit-wysiwyg ol { list-style-type: decimal !important; list-style-position: inside !important; padding-left: 1.5rem !important; margin-bottom: 1rem !important; }
              .jodit-wrapper li, .jodit-wysiwyg li { display: list-item !important; }
            `,
              }}
            />
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <JoditEditor
                  value={field.value}
                  config={joditConfig}
                  onBlur={(newContent) => field.onChange(newContent)}
                />
              )}
            />
          </div>
          {errors.description && (
            <p className="text-red-500 text-xs">{errors.description.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Seats Limit <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <Input
            type="number"
            min="1"
            placeholder="e.g. 50 — leave blank for unlimited"
            {...register('seats', {
              valueAsNumber: true,
              setValueAs: (v) => (v === '' ? '' : Number(v)),
            })}
          />
          <p className="text-xs text-muted-foreground">
            Set a maximum number of registrations. Leave blank for unlimited.
          </p>
          {errors.seats && <p className="text-red-500 text-xs">{errors.seats.message}</p>}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Registration Link (optional)</label>
          <Input placeholder="https://..." {...register('registrationLink')} />
          {errors.registrationLink && (
            <p className="text-red-500 text-xs">{errors.registrationLink.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Venue / Meeting Link (optional)</label>
          <Input
            placeholder="e.g. Room 404 / Zoom Link (hidden on portal, sent in email)"
            {...register('venue')}
            maxLength={250}
          />
          {errors.venue && <p className="text-red-500 text-xs">{errors.venue.message}</p>}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Event Thumbnail (optional)</label>
          <div className="space-y-3">
            <div className="flex items-center bg-muted rounded-lg p-0.5 w-fit text-xs">
              <button
                type="button"
                onClick={() => setThumbnailMode('file')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all ${
                  thumbnailMode === 'file'
                    ? 'bg-background text-foreground shadow-sm font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Upload size={11} /> Upload File
              </button>
              <button
                type="button"
                onClick={() => setThumbnailMode('url')}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all ${
                  thumbnailMode === 'url'
                    ? 'bg-background text-foreground shadow-sm font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Globe size={11} /> Enter URL
              </button>
            </div>

            {thumbnailMode === 'file' ? (
              <div className="flex items-center gap-4">
                {watchImageUrl && (
                  <div className="relative h-16 w-16 rounded-md overflow-hidden border shrink-0">
                    <img
                      src={watchImageUrl}
                      alt="Thumbnail"
                      className="object-cover w-full h-full"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {isUploading ? 'Uploading...' : 'Upload Image'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Link2
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    type="url"
                    className="pl-8"
                    placeholder="https://..."
                    {...register('imageUrl')}
                  />
                </div>
                {watchImageUrl && (
                  <div className="relative h-24 w-32 rounded-md overflow-hidden border">
                    <img
                      src={watchImageUrl}
                      alt="Thumbnail preview"
                      className="object-cover w-full h-full"
                      onError={(e) => (e.currentTarget.parentElement!.style.display = 'none')}
                      onLoad={(e) => (e.currentTarget.parentElement!.style.display = 'block')}
                    />
                  </div>
                )}
              </div>
            )}
            {errors.imageUrl && <p className="text-red-500 text-xs">{errors.imageUrl.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5 flex flex-col">
            <label className="text-sm font-medium">
              Date & Time <span className="text-red-500">*</span>
            </label>
            <Controller
              control={control}
              name="eventDate"
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={(date: Date | null) => field.onChange(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="MMMM d, yyyy h:mm aa"
                  customInput={<Input />}
                  wrapperClassName="w-full"
                  popperClassName="z-[100]"
                  portalId="root"
                />
              )}
            />
            {errors.eventDate && <p className="text-red-500 text-xs">{errors.eventDate.message}</p>}
          </div>
          <div className="space-y-1.5 flex flex-col">
            <label className="text-sm font-medium">Status</label>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Draft">Draft (hidden from portal)</SelectItem>
                    <SelectItem value="Published">Published (visible on portal)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.status && <p className="text-red-500 text-xs">{errors.status.message}</p>}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
